import React from 'react';
import { Users, Music, Award, AlertCircle, FileText } from 'lucide-react';

const Guidelines = () => {
  return (
    <section id="guidelines" className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">募集要項</h2>
          <div className="w-20 h-1 bg-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">2026年度 大学対抗ピアノ演奏チームコンテスト</p>
          <p className="mt-2 text-gray-600">募集大学数　10大学</p>
        </div>

        {/* 概要 */}
        <div className="bg-blue-50 p-8 rounded-xl mb-12 border border-blue-100">
          <p className="text-lg text-gray-800 leading-relaxed text-center font-medium">
            大学生のピアノサークルやピアノ同好会から選抜でチームを組成し、<br className="hidden md:inline"/>
            リレー形式でそれぞれに異なる時代やジャンルのクラシック曲を演奏する。<br className="hidden md:inline"/>
            また、その採点は各チームから選出した審査員により採点され、その合計得点を競う。
          </p>
        </div>

        {/* チーム編成・参加資格 */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center border-b-2 border-yellow-500 pb-2 inline-block">
            <Users className="mr-3 text-yellow-600" />
            チーム編成・参加資格
          </h3>
          
          <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
            <div className="p-6 md:p-8">
              <h4 className="text-lg font-bold text-gray-900 mb-4 bg-gray-100 p-3 rounded">チーム編成</h4>
              <p className="text-xl font-bold text-center mb-6 text-blue-900">1チーム6名（5名演奏者 + 審査員）</p>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8 text-center text-sm md:text-base">
                <div className="bg-pink-100 p-4 rounded-full flex items-center justify-center aspect-square flex-col border-2 border-pink-200">
                  <span className="font-bold block">第1奏者</span>
                  <span>バロック</span>
                </div>
                <div className="bg-green-100 p-4 rounded-full flex items-center justify-center aspect-square flex-col border-2 border-green-200">
                  <span className="font-bold block">第2奏者</span>
                  <span>エチュード</span>
                </div>
                <div className="bg-orange-100 p-4 rounded-full flex items-center justify-center aspect-square flex-col border-2 border-orange-200">
                  <span className="font-bold block">第3・4奏者</span>
                  <span>連弾曲</span>
                </div>
                <div className="bg-blue-100 p-4 rounded-full flex items-center justify-center aspect-square flex-col border-2 border-blue-200">
                  <span className="font-bold block">第5奏者</span>
                  <span>自由曲</span>
                </div>
                <div className="bg-indigo-100 p-4 rounded-lg flex items-center justify-center aspect-square flex-col border-2 border-indigo-200">
                  <span className="font-bold block">審査員</span>
                </div>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-4 bg-gray-100 p-3 rounded">参加資格・規定</h4>
              <ul className="list-disc list-inside space-y-3 text-gray-700 mb-6 ml-2">
                <li>チームのメンバーの1人が大学の「ピアノサークル」や「ピアノクラブ」などの団体に所属していること。</li>
                <li>同じ大学に所属する大学生、又は大学院生を参加資格とする。</li>
              </ul>

              <div className="flex flex-col md:flex-row items-center justify-center bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <span className="font-bold text-gray-700 mr-4">参加費</span>
                <span className="text-2xl font-bold text-gray-900">1大学 30,000円</span>
              </div>

              {/* 申込方法 */}
              <div className="mt-8 border-t border-gray-200 pt-8">
                <div className="text-center">
                  <p className="text-red-600 font-bold mb-4 text-lg">参加申込受付締切日/2026年5月31日(日)</p>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">申込方法</h4>
                  <p className="text-gray-700 mb-6">申込書を下記よりダウンロードしてください</p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <a 
                      href="/documents/第2回大学対抗申込書.xlsx" 
                      download 
                      className="flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                    >
                      <FileText className="mr-2 h-5 w-5" />
                      申込書 (Excel)
                    </a>
                    <a 
                      href="/documents/第2回大学対抗申込書.pdf" 
                      download 
                      className="flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                    >
                      <FileText className="mr-2 h-5 w-5" />
                      申込書 (PDF)
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 課題曲 */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center border-b-2 border-yellow-500 pb-2 inline-block">
            <Music className="mr-3 text-yellow-600" />
            2026年度 課題曲
          </h3>

          <div className="space-y-6">
            {/* 第1奏者 */}
            <div className="bg-white shadow-md rounded-lg p-6 border-l-8 border-pink-400">
              <h4 className="text-xl font-bold text-pink-600 mb-3">第1奏者：バロック課題</h4>
              <p className="text-sm text-gray-600 mb-3">下記のいずれかを選んで演奏する。</p>
              <ul className="list-disc list-outside ml-5 space-y-2 text-gray-700">
                <li>D.スカルラッティ「鍵盤楽器のためのソナタ」から任意の2～3曲選び、5分以上7分以内にまとめ演奏する。<br/><span className="text-sm text-gray-500">（演奏曲提出時にカークパトリック番号[Kk.]を明記すること）</span></li>
                <li>J.S.バッハ「平均律クラヴィーア曲集第1巻、第2巻」より任意の1曲を選んで演奏する。</li>
              </ul>
            </div>

            {/* 第2奏者 */}
            <div className="bg-white shadow-md rounded-lg p-6 border-l-8 border-green-400">
              <h4 className="text-xl font-bold text-green-600 mb-3">第2奏者：エチュード課題</h4>
              <p className="text-sm text-gray-600 mb-3">下記の練習曲から1曲を選び演奏する。</p>
              <ul className="list-disc list-outside ml-5 space-y-2 text-gray-700">
                <li>F.リスト「超絶技巧練習曲集 S.139（1番、9番、11番は除く）」</li>
                <li>F.リスト「パガニーニによる大練習曲 S.141 より 第3番、第6番」</li>
                <li>F.リスト「3つの演奏会用練習曲 S.144（1番は除く）」</li>
                <li>F.リスト「2つの演奏会用練習曲 S.145」</li>
                <li>S.ラフマニノフ「絵画的練習曲集 Op.39（2番、7番は除く）」</li>
              </ul>
            </div>

            {/* 第3・4奏者 */}
            <div className="bg-white shadow-md rounded-lg p-6 border-l-8 border-orange-400">
              <h4 className="text-xl font-bold text-orange-600 mb-3">第3・4奏者：連弾課題</h4>
              <p className="text-sm text-gray-600 mb-3">下記の3つの連弾曲集からそれぞれ1曲ずつを選び、6分以上8分以内にまとめて演奏する。</p>
              <ul className="list-disc list-outside ml-5 space-y-2 text-gray-700">
                <li>ビゼー「子供の遊び Op.22」</li>
                <li>ドヴォルザーク「スラブ舞曲第1集 Op.46」もしくは「スラブ舞曲第2集 Op.72」</li>
                <li>ブラームス「21のハンガリー舞曲集 WoO1」</li>
              </ul>
            </div>

            {/* 第5奏者 */}
            <div className="bg-white shadow-md rounded-lg p-6 border-l-8 border-blue-400">
              <h4 className="text-xl font-bold text-blue-600 mb-3">第5奏者：自由曲課題</h4>
              <p className="text-gray-700 leading-relaxed">
                自由に曲を選択し演奏する。複数曲演奏しても構わないが、6分以上8分以内にまとめること。<br/>
                ただし、著作権が切れている作曲家に限る。上記で出た課題から選ぶことは不可。
              </p>
            </div>

            {/* 注意事項 */}
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 text-sm md:text-base">
              <h5 className="font-bold text-gray-900 mb-2 flex items-center"><AlertCircle size={18} className="mr-2" /> 注意事項</h5>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>D.スカルラッティのソナタのみ繰り返しは任意とし、そのほかの曲は繰り返しはなしとする。</li>
                <li>使用楽器 &lt;ベヒシュタイン&gt;又は&lt;プレイエル&gt;フルコンサート型ピアノ</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 演奏の流れ */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center border-b-2 border-yellow-500 pb-2 inline-block">
            <FileText className="mr-3 text-yellow-600" />
            演奏の流れ及び審査方法
          </h3>
          
          <div className="relative border-l-4 border-gray-200 ml-4 md:ml-8 space-y-8 py-4">
            <div className="relative pl-8">
              <div className="absolute -left-3 top-0 bg-pink-500 w-6 h-6 rounded-full border-4 border-white shadow"></div>
              <h4 className="font-bold text-lg text-pink-600 mb-1">第1奏者 バロック</h4>
              <p className="text-gray-600 text-sm">[第1奏者]全員が演奏終了後 各大学の審査員が自大学を除き採点<br/>ステージ上のスクリーンに発表</p>
            </div>
            
            <div className="relative pl-8">
              <div className="absolute -left-3 top-0 bg-green-500 w-6 h-6 rounded-full border-4 border-white shadow"></div>
              <h4 className="font-bold text-lg text-green-600 mb-1">第2奏者 エチュード</h4>
              <p className="text-gray-600 text-sm">[第2奏者]全員が演奏終了後 各大学の審査員が自大学を除き採点<br/>ステージ上のスクリーンに発表</p>
            </div>

            <div className="relative pl-8">
              <div className="absolute -left-2 top-0 bg-gray-300 w-4 h-4 rounded-full border-2 border-white"></div>
              <h4 className="font-bold text-gray-500 mb-1">休憩</h4>
            </div>

            <div className="relative pl-8">
              <div className="absolute -left-3 top-0 bg-orange-500 w-6 h-6 rounded-full border-4 border-white shadow"></div>
              <h4 className="font-bold text-lg text-orange-600 mb-1">第3・4奏者 連弾曲</h4>
              <p className="text-gray-600 text-sm">[第3・4奏者]全員が演奏終了後 各大学の審査員が自大学を除き採点<br/>ステージ上のスクリーンに発表</p>
            </div>

            <div className="relative pl-8">
              <div className="absolute -left-3 top-0 bg-blue-500 w-6 h-6 rounded-full border-4 border-white shadow"></div>
              <h4 className="font-bold text-lg text-blue-600 mb-1">第5奏者 自由曲</h4>
              <p className="text-gray-600 text-sm">[第5奏者]全員が演奏終了後 合計点発表<br/>採点が公平になるように修正いたします</p>
            </div>
          </div>
        </div>

        {/* 表彰 */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center border-b-2 border-yellow-500 pb-2 inline-block">
            <Award className="mr-3 text-yellow-600" />
            表彰
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-gradient-to-b from-yellow-50 to-yellow-100 p-6 rounded-lg border border-yellow-200 shadow-md transform md:-translate-y-4 z-10">
              <div className="text-4xl mb-2">👑</div>
              <h4 className="text-2xl font-bold text-yellow-700 mb-2">1位</h4>
              <p className="font-bold text-gray-800">優勝旗授与<br/><span className="text-sm font-normal">(毎年持ち回り制)</span></p>
              <p className="text-gray-800 mt-2">優勝トロフィー・賞状</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-center">
              <h4 className="text-xl font-bold text-gray-600 mb-2">2位</h4>
              <p className="text-gray-800">トロフィー・賞状</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-center">
              <h4 className="text-xl font-bold text-gray-600 mb-2">3位</h4>
              <p className="text-gray-800">賞状</p>
            </div>
          </div>
          
          <div className="mt-6 text-center bg-pink-50 p-4 rounded-lg border border-pink-100">
            <p className="text-pink-800 font-medium">🎁 出場者全員に記念品を贈呈</p>
          </div>
          
          <p className="mt-4 text-xs text-gray-500 text-center">
            ※天災等、やむを得ない事情がある場合、開催を見合わせる事があります ※録音、録画不可
          </p>
        </div>

      </div>
    </section>
  );
};

export default Guidelines;
